import { useHover } from '@shared/hooks/use-hover'
import {
  type KeyboardShortCutItem,
  useKeyboardShortcuts,
} from '@shared/hooks/use-keyboard-shortcuts'
import {
  flexRender,
  type Row,
  type RowData,
  type Table as TanstackTable,
} from '@tanstack/react-table'
import { GenericContextMenu } from 'blocks/context-menu'
import { useContextMenu } from 'blocks/context-menu/use-context-menu'
import { cn } from 'lib/utils'
import { find } from 'lodash'
import { Alert } from 'primitives/alert'
import { ButtonWithLoader } from 'primitives/button'
import { ScrollArea, ScrollBar } from 'primitives/scroll-area'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'primitives/table'
import * as React from 'react'
import { useRef, useState } from 'react'
import { DEFAULT_PAGE_SIZE } from './config'
import { DataTablePagination } from './data-table-pagination'
import { DataTableToolbar } from './data-table-toolbar'
import { useDataTable, useSimpleTable } from './hooks/use-data-table'
import { DataTableSkeleton } from './skeleton'
import type { OffsetOptions, RemoteTableProps, SimpleTableProps } from './types'

declare module '@tanstack/react-table' {
  // Per-column body-cell class (e.g. 'align-top' for a checkbox column beside multi-line cells).
  // biome-ignore lint/correctness/noUnusedVariables: TS2428 — augmentation must repeat the library's exact type params
  interface ColumnMeta<TData extends RowData, TValue> {
    cellClass?: string
    // Preferred width for both the header and body cells.
    width?: string
  }
}

interface DataTableProps<TData> {
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * @type TanstackTable<TData>
   */
  table: TanstackTable<TData>
  idKey?: string
  onRowClick?: (row: TData) => void
  onRowContextMenu?: (row: TData, event: React.MouseEvent) => void
  cellClass?: string
  pageSizeOptions?: number[]
  defaultPageSize?: number
  offsetOptions?: OffsetOptions
  paginated?: boolean
  /**
   * A <colgroup> pinning column widths. Supplying one also switches the table
   * to fixed layout, so columns hold position instead of resizing to their
   * content. Omitted by default, leaving the browser's auto sizing untouched.
   */
  colGroup?: React.ReactNode | null
}

const DataTable = <TData,>({
  table,
  onRowClick,
  onRowContextMenu,
  idKey,
  cellClass,
  pageSizeOptions,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  offsetOptions = { isOffsetMode: false, hasNext: false, hasPrevious: false },
  paginated = true,
  colGroup,
}: DataTableProps<TData>) => (
  <div className="w-full space-y-2.5 min-h-0 flex flex-col">
    <ScrollArea className="rounded-md min-h-0 flex-1">
      <div className="relative w-full">
        <table className={cn('w-full caption-bottom text-s-400', colGroup && 'table-fixed')}>
          {colGroup}
          <TableHeader className="sticky top-0 bg-background z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="p-2 border-collapse border-b h-6 box-content text-secondary"
                    style={
                      header.column.columnDef.meta?.width
                        ? {
                            width: header.column.columnDef.meta.width,
                            minWidth: header.column.columnDef.meta.width,
                          }
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  tabIndex={0}
                  style={{ outlineOffset: -1 }}
                  data-state={row.getIsSelected() && 'selected'}
                  data-hover={(row.original as Record<string, string>)[idKey || 'id']}
                  onClick={() => {
                    // Don't trigger onRowClick if user is selecting text
                    const selection = window.getSelection()
                    if (selection && selection.toString().length > 0) {
                      return
                    }
                    onRowClick?.(row.original)
                  }}
                  onContextMenu={(e) => {
                    onRowContextMenu?.(row.original, e)
                  }}
                  className="cursor-pointer hover:bg-bg2 dark:hover:bg-bg1 group"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={
                        cell.column.columnDef.meta?.width
                          ? {
                              width: cell.column.columnDef.meta.width,
                              minWidth: cell.column.columnDef.meta.width,
                            }
                          : undefined
                      }
                      className={cn(
                        'border-collapse px-2 py-2.5 h-6 box-content truncate max-w-64',
                        cellClass,
                        cell.column.columnDef.meta?.cellClass,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-32 text-center text-s-400 text-secondary"
                >
                  No data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
    {paginated &&
      table.getRowModel().rows.length > 0 &&
      (offsetOptions.isOffsetMode
        ? offsetOptions.hasNext || offsetOptions.hasPrevious
        : table.getRowCount() !== undefined && table.getRowCount() > defaultPageSize) && (
        <div className="flex flex-col gap-2.5">
          <DataTablePagination
            table={table}
            pageSizeOptions={pageSizeOptions}
            offsetOptions={offsetOptions}
          />
        </div>
      )}
  </div>
)

export const findRowFromRows = <T,>(rows: Row<T>[] | undefined, obj: Partial<T>) => {
  const row = find(rows, { original: obj }) as Row<T>
  return row?.original || undefined
}

export const RemoteTable = <TData, TApi>(props: RemoteTableProps<TData, TApi>) => {
  const {
    filterFields,
    ActionButton,
    isLoading,
    cellClass,
    pageSizeOptions,
    emptyState,
    renderError,
    defaultPageSize,
    onTableReady,
  } = props
  const { table, isPending, error, refetch, queryIdentity, offsetOptions } = useDataTable(props)
  const [retryAttempt, setRetryAttempt] = useState<{ queryIdentity: string } | null>(null)
  const isRetrying = retryAttempt?.queryIdentity === queryIdentity
  const contextOpenRowRef = useRef<TData | undefined>(undefined)
  const [hoveredElementRef, hoverParentRef, setHoveredElement] = useHover()

  const { onRowClick, idKey, getContextMenu } = props
  const paginationRowModel = table.getPaginationRowModel()
  const { menuItems, updateMenu } = useContextMenu<TData>()

  // call onTableReady when table is ready
  React.useEffect(() => {
    onTableReady?.(table)
  }, [])

  React.useEffect(() => {
    updateMenu(
      getContextMenu?.(contextOpenRowRef.current, paginationRowModel.rows, hoveredElementRef) || [],
    )
  }, [paginationRowModel.rows])

  // Keyboard shortcuts for the context menu on the rows
  useKeyboardShortcuts(menuItems as KeyboardShortCutItem[])

  const handleRetry = async () => {
    const attempt = { queryIdentity }
    setRetryAttempt(attempt)
    try {
      await refetch()
    } finally {
      // A slow retry for filter A must not clear the pending retry for filter B.
      setRetryAttempt((current) => (current === attempt ? null : current))
    }
  }

  // A retry clears the query error before it settles. Keep the failed-read
  // presentation mounted so the pending action cannot turn into an empty table.
  const showDefaultError = !renderError && (Boolean(error) || isRetrying)
  const hasRows = table.getRowModel().rows.length > 0
  const hasData = table.getCoreRowModel().rows.length > 0

  if ((isLoading || isPending) && !showDefaultError)
    return <DataTableSkeleton columnCount={table.getAllColumns().length} />

  const errorNode = error && renderError ? renderError(error) : null
  if (!errorNode && !showDefaultError && !hasData && emptyState) return emptyState

  return (
    <>
      <DataTableToolbar table={table} filterFields={filterFields}>
        {ActionButton}
      </DataTableToolbar>
      {showDefaultError && (
        <Alert
          variant="destructive"
          title={hasRows ? "Couldn't refresh this list." : "Couldn't load this list."}
          className="mb-4"
          content={
            <div className="space-y-2">
              {hasRows && <p>Previously loaded results are shown below.</p>}
              <ButtonWithLoader
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRetry}
                loading={isRetrying}
                aria-busy={isRetrying}
                data-testid="button-retry-table"
              >
                Retry
              </ButtonWithLoader>
            </div>
          }
        />
      )}
      {/* An error keeps the toolbar so the offending filter can be corrected in place. */}
      {errorNode ??
        ((!showDefaultError || hasRows) && (
          <GenericContextMenu menuItems={menuItems} className="flex flex-col min-h-0">
            <div className="flex flex-col min-h-0" ref={hoverParentRef}>
              <DataTable
                table={table}
                onRowClick={onRowClick}
                onRowContextMenu={(row, e) => {
                  setHoveredElement(e.target as HTMLElement)
                  contextOpenRowRef.current = row
                  // update the context menu items with the row data on right click
                  updateMenu(
                    getContextMenu?.(row, paginationRowModel.rows, hoveredElementRef) || [],
                  )
                }}
                cellClass={cellClass}
                idKey={idKey}
                pageSizeOptions={pageSizeOptions}
                defaultPageSize={defaultPageSize}
                offsetOptions={offsetOptions}
                colGroup={props.colGroup}
              />
            </div>
          </GenericContextMenu>
        ))}
    </>
  )
}

export const SimpleTable = <TData,>(props: SimpleTableProps<TData>) => {
  const { table } = useSimpleTable(props)
  if (props.isLoading) return <DataTableSkeleton columnCount={props.columns.length} />
  return <DataTable table={table} cellClass="h-6" paginated={false} />
}
